import moment from "moment";
import { INITIATIVE_TYPE_OPTIONS } from "../../ambassadors/ambassador-details/initiatives/utils";

export const INITIATIVE_TYPE_VALUES = INITIATIVE_TYPE_OPTIONS.map(
  (option) => option.value
);

export const TARGETED_COMPLETION_OVER_1_YEAR_VALUE = "over_1_year";

export type InitiativeFormValues = {
  title: string;
  type: string;
  toolName: string | string[];
  summary: string;
  plannedDeliveryDate: string;
  isPublic: boolean;
  ambassadorId: string;
};

export const DEFAULT_INITIATIVE_FORM_VALUES: InitiativeFormValues = {
  title: "",
  type: "new purchase",
  toolName: "",
  summary: "",
  plannedDeliveryDate: "",
  isPublic: true,
  ambassadorId: "",
};

export function getDefaultPlannedDeliveryDate(): string {
  return moment().add(12, "months").startOf("month").format("YYYY-MM");
}

export function plannedDeliveryDateToPayload(formValue: string): string {
  if (formValue === TARGETED_COMPLETION_OVER_1_YEAR_VALUE) {
    return moment().add(13, "months").endOf("month").format("YYYY-MM-DD");
  }
  return moment(formValue, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
}

export function plannedDeliveryDateToFormValue(apiDate: string): string {
  if (!apiDate) return "";
  const parsed = moment(apiDate);
  if (!parsed.isValid()) return "";
  return parsed.format("YYYY-MM");
}

export function toolNameToString(
  value: string | string[] | undefined | null
): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean)
      .join(", ");
  }
  return typeof value === "string" ? value : "";
}

export function toolNameToChipValues(
  value: string | string[] | undefined | null
): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function initiativeToFormValues(initiative: any): InitiativeFormValues {
  return {
    title: initiative?.title ?? "",
    type: (initiative?.type ?? "").toLowerCase() || "new purchase",
    toolName: toolNameToChipValues(initiative?.toolName),
    summary: initiative?.summary ?? "",
    plannedDeliveryDate: plannedDeliveryDateToFormValue(
      initiative?.plannedDeliveryDate
    ),
    isPublic: Boolean(initiative?.isPublic),
    ambassadorId: initiative?.ambassadorId ?? "",
  };
}

export function buildInitiativePayload(
  data: InitiativeFormValues,
  { includeAmbassadorId = true }: { includeAmbassadorId?: boolean } = {}
): Record<string, unknown> {
  const plannedDeliveryDate =
    data.plannedDeliveryDate?.trim() || getDefaultPlannedDeliveryDate();
  const toolName = toolNameToString(data.toolName).trim();
  const isReplaceTool = data.type?.toLowerCase() === "replace tool";

  const payload: Record<string, unknown> = {
    title: data.title.trim(),
    type: data.type.toLowerCase(),
    summary: data.summary.trim(),
    plannedDeliveryDate: plannedDeliveryDateToPayload(plannedDeliveryDate),
    isPublic: Boolean(data.isPublic),
  };

  if (includeAmbassadorId) {
    payload.ambassadorId = data.ambassadorId;
  }

  if (isReplaceTool && toolName) {
    payload.toolName = toolName;
  }

  return payload;
}

export type InitiativeFormErrors = Partial<
  Record<keyof InitiativeFormValues, string>
>;

export function validateInitiativeForm(
  data: InitiativeFormValues,
  { requireAmbassador }: { requireAmbassador: boolean }
): InitiativeFormErrors {
  const errors: InitiativeFormErrors = {};

  if (!data.title?.trim()) {
    errors.title = "Title is required";
  }

  if (!data.type?.trim()) {
    errors.type = "Type is required";
  } else if (!INITIATIVE_TYPE_VALUES.includes(data.type.toLowerCase())) {
    errors.type = "Invalid type";
  }

  if (!data.summary?.trim()) {
    errors.summary = "Summary is required";
  }

  if (data.type?.toLowerCase() === "replace tool") {
    const toolName = toolNameToString(data.toolName).trim();
    if (!toolName) {
      errors.toolName = "Please enter the tool you are replacing";
    }
  }

  if (requireAmbassador && !data.ambassadorId) {
    errors.ambassadorId = "Please select an ambassador";
  }

  return errors;
}
